import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  Avatar,
  Badge,
  Menu,
  Tooltip,
  ListItemButton,
  ListItemText,
  Button,
  List,
  ListItem,
  ListItemIcon,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import RudderLogo from "../img/Starlight Logo.png";
import {
  RiAlertFill,
  RiBardFill,
  RiBookReadFill,
  RiBugFill,
  RiErrorWarningFill,
  RiMovieFill,
  RiNotification2Line,
  RiQuestionFill,
  RiQuestionLine,
} from "@remixicon/react";
import SearchSite from "../components/general/SearchSite";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppRoutes } from "../routes/AppRoutes";
import useFetcher from "../hooks/useFetcher";

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: "transparent",
  color: theme.palette.common.black,
}));

function AppHeader(props) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = React.useState(null);
  const openProfileMenu = Boolean(profileMenuAnchorEl);

  const [heplMenuAnchorEl, setHeplMenuAnchorEl] = React.useState(null);
  const [notiMenuAnchorEl, setNotiMenuAnchorEl] = React.useState(null);

  const openHelpMenu = Boolean(heplMenuAnchorEl);
  const openNotiMenu = Boolean(notiMenuAnchorEl);

  const { fetch: fetchService } = useFetcher();

  const username = localStorage.getItem("username");

  const displayName =
    username && username != null ? username?.substring(0, 2) : "AA";

  const user = useSelector((state) => state.auth?.user);

  const handleClickProfileMenu = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileMenuAnchorEl(null);
    navigate(AppRoutes.ADMINISTRATION.USERS.UPDATE_PROFILE);
  };

  const handleCloseMenu = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleCloseChangePassword = () => {
    setProfileMenuAnchorEl(null);
    navigate(AppRoutes.ADMINISTRATION.USERS.FORCE_CHANGE_PASSWORD);
  };

  const handleClickHelpMenu = (event) => {
    setHeplMenuAnchorEl(event.currentTarget);
  };

  const handleCloseHelpMenu = () => {
    setHeplMenuAnchorEl(null);
  };

  const handleClickNotiMenu = (event) => {
    setNotiMenuAnchorEl(event.currentTarget);
  };

  const handleCloseNotiMenu = () => {
    setNotiMenuAnchorEl(null);
  };

  const helpMenu = [
    {
      menuName: "What's New",
      menuIcon: <RiBardFill style={{ height: 20, width: 20 }} />,
    },
    {
      menuName: "Documentation",
      menuIcon: <RiBookReadFill style={{ height: 20, width: 20 }} />,
    },
    {
      menuName: "Watch Setup Videos",
      menuIcon: <RiMovieFill style={{ height: 20, width: 20 }} />,
    },
    {
      menuName: "FAQ",
      menuIcon: <RiQuestionFill style={{ height: 20, width: 20 }} />,
    },
    {
      menuName: "Report Bug",
      menuIcon: <RiBugFill style={{ height: 20, width: 20 }} />,
    },
  ];

  // Logout
  const logout = async () => {
    setProfileMenuAnchorEl(null);

    try {
      await fetchService({
        method: "POST",
        url: "auth/logout",
        data: user,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.clear();
      navigate("/");
    }
  };

  return (
    <AppBar position="fixed" elevation={0}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={props.drawerToggle}
          edge="start"
          sx={{
            marginRight: 2,
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div">
          <img className="" height={35} src={RudderLogo} alt="" />
        </Typography>

        <Divider
          orientation="vertical"
          sx={{
            height: 36,
            mx: 2,
            background: theme.palette.grey.A700,
          }}
        />

        <SearchSite />

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: { xs: "none", md: "flex" } }}>
          <IconButton
            size="large"
            aria-label="alert"
            color="inherit"
            onClick={props.alertToggle}
          >
            <RiAlertFill color={theme.palette.warning.main} />
          </IconButton>

          <Tooltip title="Notifications">
            <IconButton
              size="large"
              aria-label="show 4 new mails"
              color="inherit"
              onClick={handleClickNotiMenu}
            >
              <Badge badgeContent={4} color="error">
                                {/* <MailIcon /> */}
                <RiNotification2Line />
              </Badge>
            </IconButton>
          </Tooltip>

          <Menu
            id="basic-menu"
            anchorEl={notiMenuAnchorEl}
            open={openNotiMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={handleCloseNotiMenu}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  padding: 0,
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "& .MuiList-root": {
                    paddingTop: 0,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 20,
                    width: 10,
                    height: 10,
                    bgcolor: "primary.main",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <div
              className="d-flex justify-content-between px-3 py-2 align-items-center"
              style={{
                borderRadius: "16px 16px 0 0",
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }}
            >
              <Typography variant="overline" fontWeight="300">
                Top Notifications
              </Typography>

              <Button
                size="small"
                className="text-white text-decoration-underline"
                variant="text"
              >
                See All
              </Button>
            </div>

            <List
              sx={{ width: 400, maxHeight: 300 }}
              className="notification-list overflow-auto pt-3"
            >
              <ListItem className="p-0">
                <ListItemButton className="py-0" alignItems="flex-start">
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: "warning.main",
                    }}
                  >
                    <RiErrorWarningFill />
                  </ListItemIcon>

                  <ListItemText
                    primary="Notification Name"
                    secondary="Short Description"
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Menu>

          <Tooltip title="Help">
            <IconButton
              size="large"
              aria-label="show help menu"
              color="inherit"
              onClick={handleClickHelpMenu}
            >
              <RiQuestionLine />
            </IconButton>
          </Tooltip>

          <Menu
            id="basic-menu"
            anchorEl={heplMenuAnchorEl}
            open={openHelpMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={handleCloseHelpMenu}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 20,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {helpMenu.map((item, index) => (
              <MenuItem
                key={index}
                className="py-2"
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                }}
                onClick={handleCloseHelpMenu}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {item.menuIcon}
                </ListItemIcon>

                {item.menuName}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title="Account settings">
            <IconButton
              onClick={handleClickProfileMenu}
              size="small"
              aria-haspopup="true"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: theme.palette.primary.main,
                  textTransform: "uppercase",
                }}
              >
                <Typography variant="body2">{displayName}</Typography>
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            id="basic-menu"
            anchorEl={profileMenuAnchorEl}
            open={openProfileMenu}
            onClose={handleCloseMenu}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem onClick={handleCloseProfileMenu}>Profile</MenuItem>

            <MenuItem onClick={handleCloseChangePassword}>
              Change Password
            </MenuItem>

            <Divider />

            <MenuItem onClick={logout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default AppHeader;
